import {Component, computed, input} from '@angular/core';
import {faComment} from '@fortawesome/pro-solid-svg-icons';
import {Tooltip} from 'primeng/tooltip';

const [ICON_WIDTH, ICON_HEIGHT, , , ICON_PATH] = faComment.icon;

/* Extents of the bubble's rounded body, read off faComment's path: its four arcs meet at
   (512,240), (256,480), (0,240) and (256,0), each with a horizontal/vertical tangent there. The tail
   (below y=480, bottom-left) is excluded so the count centres on the body, not the whole glyph. */
const BODY_LEFT = 0;
const BODY_RIGHT = 512;
const BODY_TOP = 0;
const BODY_BOTTOM = 480;

/** Inter's cap height (1490 of 2048 units). Half of it below the centre puts digits' middle there. */
const INTER_CAP_HEIGHT_EM = 1490 / 2048;

/** Count size as a fraction of the icon's height: 99+ is a third character in a bubble sized for two. */
const COUNT_SIZE = 0.45;
const COUNT_SIZE_3_CHARS = 0.36;

/**
 * The comment indicator - a filled speech bubble with the number of comments inlaid in it. Used by
 * the TA list tables and by the comment chain's own heading. Draws nothing when comment count is 0.
 */
@Component({
  selector: 'ta-comment-count',
  imports: [Tooltip],
  template: `
    @if (hasComments() || showZero()) {
    <span class="inline-flex items-center justify-center align-middle leading-none
                 whitespace-nowrap text-[1.6em]"
          [class.text-surface-400]="!hasComments()"
          [class.text-teal-500]="hasComments()"
          [pTooltip]="tooltip()"
          tooltipPosition="left"
          data-testid="ta-comment-count">
      <!-- Bubble and count share one viewBox, so the count can't drift off the bubble's centre. -->
      <svg class="block size-[1em] overflow-visible" [attr.viewBox]="viewBox" fill="currentColor">
        <path [attr.d]="path"/>
        <text class="fill-white font-bold tabular-nums select-none pointer-events-none"
              font-family="Inter, sans-serif" text-anchor="middle"
              [attr.x]="centreX" [attr.y]="centreY" [attr.dy]="baselineOffset"
              [attr.font-size]="fontSize()">{{ label() }}</text>
      </svg>
    </span>
    }
  `,
})
export class TaCommentCount {
  /** Comments on the TA, from the list row. Null/undefined is treated as none. */
  count = input<number | undefined | null>(undefined);
  /** Draw the badge on a TA with no comments too, greyed out. For hosts that need it always there. */
  showZero = input<boolean>(false);

  protected readonly viewBox = `0 0 ${ICON_WIDTH} ${ICON_HEIGHT}`;
  protected readonly path = ICON_PATH as string;
  protected readonly centreX = (BODY_LEFT + BODY_RIGHT) / 2;
  protected readonly centreY = (BODY_TOP + BODY_BOTTOM) / 2;
  // dy rather than dominant-baseline, which Safari applies inconsistently.
  protected readonly baselineOffset = `${INTER_CAP_HEIGHT_EM / 2}em`;

  commentCount = computed<number>(() => this.count() ?? 0);
  hasComments = computed<boolean>(() => this.commentCount() > 0);
  /** Capped at three characters so a runaway chain still fits; the tooltip keeps the real number. */
  label = computed<string>(() => (this.commentCount() > 99 ? '99+' : `${this.commentCount()}`));
  fontSize = computed<number>(() => (this.label().length > 2 ? COUNT_SIZE_3_CHARS : COUNT_SIZE) * ICON_HEIGHT);
  tooltip = computed<string>(() => {
    const count = this.commentCount();
    if (count === 0) {
      return 'No comments';
    }
    return count === 1 ? '1 comment' : count + ' comments';
  });
}
